import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import moment from 'moment-timezone';
import { Input } from '@rebass/forms';

import ComponentHeader from './component-header';
import ComponentLabel from './component-label';
import helpersScreener from 'utils/helpers-screener';
import { getElementLabel } from './getElementLabel';
import { DateInput } from 'components/DateInput';
import { getDisplayFormat, isDateTimeQuestion } from 'utils/dateFormats';

/**
 * Formats the stored answer for the read-only view.
 *
 * Datetime answers (`showTimeSelect=true`) are an absolute instant. `DateInput`
 * now submits them as ISO 8601 UTC (which `moment(...)` parses natively); older
 * legacy answers were submitted as naive `"MM/DD/YYYY hh:mm A"` browser-local
 * strings. `moment(value)` handles both: it recognizes the ISO `Z` and uses
 * the absolute moment, and for the legacy local string it falls back to
 * browser-local parsing (matching how `StudyParticipantsList`'s screener-answer
 * column already renders the same value via `new Date(value)` + Intl). We then
 * `.tz(timezone)` to localize to the researcher's profile TZ — keeping read
 * mode aligned with the study table and `Answered At`.
 *
 * Date-only and time-only answers are intentionally TZ-agnostic: shifting them
 * by the profile offset would change the calendar day or clock time the
 * participant picked. When no timezone is supplied (participant view) we just
 * format in browser-local so nothing about the participant flow changes.
 *
 * @param {string|Date|undefined} value - Stored answer value
 * @param {string|undefined} timezone - IANA timezone (researcher's profile TZ)
 * @param {boolean} showTimeSelectOnly - Whether the question is time-only
 * @param {boolean} showTimeSelect - Whether the question includes time
 * @returns {string} Formatted value or '' when value is falsy/invalid
 */
export const formatReadOnlyValue = (value, timezone, showTimeSelectOnly, showTimeSelect) => {
    if (!value) return '';

    const format = getDisplayFormat(showTimeSelectOnly, showTimeSelect);
    const isDateTime = isDateTimeQuestion(showTimeSelectOnly, showTimeSelect);

    let parsed = timezone && isDateTime ? moment(value).tz(timezone) : moment(value);
    // Time-only answers like "11:11 AM" aren't recognized by moment's default
    // heuristics — same fallback `getInitialValue` uses.
    if (!parsed.isValid() && showTimeSelectOnly) {
        parsed = moment(value, 'hh:mm A');
    }

    return parsed.isValid() ? parsed.format(format) : '';
};

/**
 * Initializes the date value based on defaultToday flag and defaultValue prop
 * @param {boolean} defaultToday - Whether to use current date as default
 * @param {string|Date} defaultValue - Default value from props
 * @returns {Date|undefined} Initialized Date object or undefined
 */
const getInitialValue = (defaultToday, defaultValue) => {
    if (defaultToday && (defaultValue === '' || defaultValue === undefined)) {
        return new Date();
    } else if (defaultValue) {
        let momentValue = moment(defaultValue);

        // Fallback for time-only strings like "11:11 AM" which moment can't parse without format
        if (!momentValue.isValid()) {
            momentValue = moment(defaultValue, 'hh:mm A');
        }

        if (momentValue.isValid()) {
            return momentValue.toDate();
        }
    }
    return undefined;
};

/**
 * @typedef {Object} DatePickerData
 * @property {boolean} [showTimeSelect] - Whether to show time selection with date
 * @property {boolean} [showTimeSelectOnly] - Whether to show only time selection
 * @property {boolean} [defaultToday] - Whether to default to today's date
 * @property {string} field_name - Field name for form submission
 * @property {boolean} [readOnly] - Whether the input is read-only
 * @property {boolean} [pageBreakBefore] - Whether to add page break before element
 * @property {string} id - Element ID
 */

/**
 * @typedef {Object} DatePickerProps
 * @property {DatePickerData} data - Element configuration data
 * @property {string|Date} [defaultValue] - Default value
 * @property {boolean} [read_only] - Whether the input is read-only
 * @property {boolean} [mutable] - Whether the element is mutable
 * @property {Array} [all_questions] - All questions for logic evaluation
 * @property {number} [page_index] - Current page index
 * @property {Object} [answers_by_form_element_name] - Answers by element name
 * @property {boolean} [show_qualification] - Whether to show qualification
 * @property {JSX.Element} [editElement] - Edit element to render instead
 * @property {Function} [handleChange] - Change handler from parent
 * @property {boolean} [isQuestionLogic] - Whether question logic is enabled
 * @property {string} [timezone] - IANA timezone to anchor the read-only display in
 *                                 (the researcher's profile TZ). When provided, the
 *                                 stored value is parsed as UTC and converted to this
 *                                 timezone — keeping the read-only view aligned with
 *                                 other date renderings (e.g. the StudyParticipantsList
 *                                 panel-property column via `useProtectedCompactRender`).
 *                                 Ignored when participants fill in the form.
 */

/**
 * Date picker form element with segmented input and calendar
 * @param {DatePickerProps} props - Component props
 * @param {React.Ref} ref - Forwarded ref for parent access
 * @returns {JSX.Element|null} Rendered component or null if hidden by logic
 */
const DatePicker = forwardRef((props, ref) => {
    const {
        data,
        defaultValue,
        read_only,
        mutable,
        all_questions,
        page_index,
        answers_by_form_element_name,
        show_qualification,
        editElement,
        handleChange,
        isQuestionLogic,
        timezone
    } = props;

    const { showTimeSelect, showTimeSelectOnly, defaultToday, field_name, readOnly, pageBreakBefore, id } = data;

    const inputField = useRef(null);
    const [value, setValue] = useState(() => getInitialValue(defaultToday, defaultValue));

    /**
     * Handles date changes from the DateInput and notifies parent
     * @param {Date|null} date - New date value
     */
    const handleDateInputChange = date => {
        setValue(date);
        if (handleChange) {
            handleChange();
        }
    };

    const isReadOnly = readOnly || read_only;

    let baseClasses = 'SortableItem rfb-item';
    if (pageBreakBefore) {
        baseClasses += ' alwaysbreak';
    }

    const inputProps = {
        name: field_name
    };

    if (mutable) {
        inputProps.defaultValue = defaultValue;
        inputProps.ref = inputField;
    }

    const shouldShow = helpersScreener.checkQuestionLogic(
        data,
        all_questions,
        page_index,
        answers_by_form_element_name,
        show_qualification
    );

    /**
     * Exposes inputField ref to parent components.
     * form.jsx reads the formatted value directly from inputField.current.value
     * (the hidden input in DateInput).
     */
    useImperativeHandle(ref, () => ({ inputField }), []);

    useEffect(() => {
        setValue(getInitialValue(defaultToday, defaultValue));
    }, [defaultToday, defaultValue]);

    if (!shouldShow && !editElement) {
        return null;
    }

    return (
        <div className={baseClasses}>
            {editElement || (
                <div>
                    <ComponentHeader {...props} />
                    <div className="form-group">
                        <ComponentLabel {...props} isQuestionLogicVisible={show_qualification && isQuestionLogic} />
                        <div className="relative">
                            {isReadOnly ? (
                                <Input
                                    type="text"
                                    name={inputProps.name}
                                    ref={inputProps.ref}
                                    readOnly
                                    value={formatReadOnlyValue(
                                        defaultValue,
                                        timezone,
                                        showTimeSelectOnly,
                                        showTimeSelect
                                    )}
                                    className="form-control"
                                    aria-labelledby={getElementLabel(id)}
                                />
                            ) : (
                                <DateInput
                                    value={value}
                                    onChange={handleDateInputChange}
                                    ariaLabelledby={getElementLabel(id)}
                                    showTimeSelect={showTimeSelect}
                                    showTimeSelectOnly={showTimeSelectOnly}
                                    name={inputProps.name}
                                    inputRef={inputProps.ref}
                                    timezone={timezone}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default DatePicker;
